/**
 * Runtime recolour: applying a look's hex→hex table to a hero frame.
 *
 * The table comes from shared/palette.ts `colourLut` — DATA, produced from the
 * committed catalogue. There is no colour math here and none at render time:
 * one pass over the frame's pixels replaces any OPAQUE pixel whose RGB exactly
 * matches a canon class tone, and leaves everything else alone (transparent
 * pixels, antialiased edges, gear metal, the outline). That is exactly the
 * operation the pipeline's bake performed per file — recolour_gate.py proved
 * the two are byte-identical over all 20,160 baked frames, so a live LUT and
 * the committed tint trees are the same picture.
 *
 * One implementation: the game path (hero.ts `drawHero` with an appearance)
 * and the palette editor / creator preview both apply looks through this
 * module. The sim never sees a look.
 */
import { colourLut, type PaletteCatalog, type RegionColours } from "@pd/shared";

/** A packed look table, ready to apply. A Map, not a flat array: the key is a
 *  full 24-bit RGB (exactness is the point), and a look touches at most a few
 *  dozen colours — a fixed table would need 2^24 entries for no benefit. */
export interface HeroLut {
  /** Stable signature of the look ("hair=2;legs=1;skin=3;top=4") — labels, logs. */
  sig: string;
  /** rgb key (r<<16 | g<<8 | b) → packed replacement rgb (same layout). */
  lut: Map<number, number>;
}

const hexToRgbKey = (hex: string): number =>
  (parseInt(hex.slice(1, 3), 16) << 16) | (parseInt(hex.slice(3, 5), 16) << 8) | parseInt(hex.slice(5, 7), 16);

/**
 * Pack a look into an apply table. Null when the look recolours nothing
 * (all-canonical or unresolvable ids) — the caller then skips the pixel pass
 * entirely and the hero wears the committed canon frames. `cat` lets the
 * editor preview draft swatches; the game passes the committed catalogue.
 */
export function packHeroLut(colours: RegionColours, cat?: PaletteCatalog): HeroLut | null {
  const map = colourLut(colours, cat);
  if (map.size === 0) return null;
  const lut = new Map<number, number>();
  for (const [from, to] of map) lut.set(hexToRgbKey(from), hexToRgbKey(to));
  const sig = Object.entries(colours)
    .filter(([, id]) => id !== undefined && id !== 0)
    .map(([k, id]) => `${k}=${id}`)
    .sort()
    .join(";");
  return { sig: sig || "canon", lut };
}

/**
 * Apply a table to one RGBA pixel buffer, IN PLACE. The pure core: no DOM, so
 * it unit-tests without a browser. `d` is the ImageData.data of a w*h frame.
 * Only opaque pixels are candidates — a translucent antialiased edge must keep
 * blending exactly as the unbaked art does (the bake's remap_image worked the
 * same way). Returns the number of pixels replaced.
 */
export function applyLutToPixels(d: Uint8ClampedArray, lut: Map<number, number>): number {
  let n = 0;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] !== 255) continue;
    const k = (d[i]! << 16) | (d[i + 1]! << 8) | d[i + 2]!;
    const v = lut.get(k);
    if (v === undefined) continue;
    d[i] = (v >> 16) & 255;
    d[i + 1] = (v >> 8) & 255;
    d[i + 2] = v & 255;
    n += 1;
  }
  return n;
}

